import { isTokenExpired } from '../utils/auth';
import { createFormData } from '../utils/fileUpload';

// Swagger curl 명령어 기준: http://localhost:4000/color/admin/login
// 따라서 base URL은 http://localhost:4000/color
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/color';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.onTokenExpired = null; // 토큰 만료 시 콜백 함수
  }

  // 토큰 만료 콜백 설정
  setTokenExpiredCallback(callback) {
    this.onTokenExpired = callback;
  }

  // 공통 헤더 설정 (파일 업로드 시에는 Content-Type 제외)
  getHeaders(includeContentType = true) {
    const token = localStorage.getItem('adminToken');
    
    // 토큰이 만료되었는지 확인
    if (token && isTokenExpired(token)) {
      console.log('Token expired, triggering logout...');
      this.handleTokenExpired();
      return {};
    }
    
    const headers = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // 토큰 만료 처리
  handleTokenExpired() {
    this.logout();
    if (this.onTokenExpired) {
      this.onTokenExpired();
    }
  }

  // API 응답에서 토큰 만료 확인
  async handleResponse(response) {
    if (response.status === 401) {
      console.log('Unauthorized response, token may be expired');
      this.handleTokenExpired();
      throw new Error('Authentication failed');
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return response.json();
  }

  // 로그인
  // Swagger curl: http://localhost:4000/color/admin/login
  async login(username, password) {
    try {
      // baseURL이 http://localhost:4000/color 이면 /admin/login만 추가
      // baseURL이 http://localhost:4000 이면 /color/admin/login 추가
      const loginUrl =  `${this.baseURL}/admin/login`;
      console.log('=== LOGIN DEBUG ===');
      console.log('API Base URL:', this.baseURL);
      console.log('Login URL:', loginUrl);
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify({ email: username, password }),
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`로그인 실패 (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Login success:', data);
      
      if (data.access_token) {
        localStorage.setItem('adminToken', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }
        console.log('Token saved successfully');
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // 공지사항 관련 API
  async getNotices(page = 1, limit = 1000) {
    const response = await fetch(`${this.baseURL}/notices?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getNoticeById(id) {
    const response = await fetch(`${this.baseURL}/notices/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createNotice(noticeData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(noticeData);
    
    console.log('=== NOTICE CREATE DEBUG ===');
    console.log('Request URL:', `${this.baseURL}/notices`);
    console.log('Sending notice data:', noticeData);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    console.log('Headers:', this.getHeaders(false));
    
    try {
      const response = await fetch(`${this.baseURL}/notices`, {
        method: 'POST',
        headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
        body: formData,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create notice error:', error);
      throw error;
    }
  }

  async updateNotice(id, noticeData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(noticeData);
    const response = await fetch(`${this.baseURL}/notices/${id}`, {
      method: 'PATCH', // PUT 대신 PATCH 사용
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async deleteNotice(id) {
    const response = await fetch(`${this.baseURL}/notices/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 언론보도 관련 API
  async getPressList(page = 1, limit = 1000) {
    const response = await fetch(`${this.baseURL}/press?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getPressById(id) {
    const response = await fetch(`${this.baseURL}/press/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createPress(pressData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(pressData);
    
    console.log('Sending press data:', pressData);
    console.log('FormData:', formData);
    
    const response = await fetch(`${this.baseURL}/press`, {
      method: 'POST',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    
    return this.handleResponse(response);
  }

  async updatePress(id, pressData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(pressData);
    
    const response = await fetch(`${this.baseURL}/press/${id}`, {
      method: 'PATCH', // PUT 대신 PATCH 사용
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async deletePress(id) {
    const response = await fetch(`${this.baseURL}/press/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 세미나 관련 API
  async getSeminars(page = 1, limit = 1000) {
    const response = await fetch(`${this.baseURL}/seminars?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getSeminarById(id) {
    const response = await fetch(`${this.baseURL}/seminars/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createSeminar(seminarData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(seminarData);
    
    console.log('=== SEMINAR CREATE DEBUG ===');
    console.log('Request URL:', `${this.baseURL}/seminars`);
    console.log('Sending seminar data:', seminarData);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    console.log('Headers:', this.getHeaders(false));
    
    try {
      const response = await fetch(`${this.baseURL}/seminars`, {
        method: 'POST',
        headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
        body: formData,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create seminar error:', error);
      throw error;
    }
  }

  async updateSeminar(id, seminarData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(seminarData);
    const response = await fetch(`${this.baseURL}/seminars/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async deleteSeminar(id) {
    const response = await fetch(`${this.baseURL}/seminars/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }
  // 연혁 관련 API
  async getHistory(page = 1, limit = 1000) {
    const response = await fetch(`${this.baseURL}/history?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createHistory(historyData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(historyData);
    
    console.log('=== HISTORY CREATE DEBUG ===');
    console.log('Request URL:', `${this.baseURL}/history`);
    console.log('Sending history data:', historyData);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    console.log('Headers:', this.getHeaders(false));
    
    try {
      const response = await fetch(`${this.baseURL}/history`, {
        method: 'POST',
        headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
        body: formData,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create history error:', error);
      throw error;
    }
  }

  async updateHistory(id, historyData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(historyData);
    
    const response = await fetch(`${this.baseURL}/history/${id}`, {
      method: 'PATCH', // PUT 대신 PATCH 사용
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async deleteHistory(id) {
    const response = await fetch(`${this.baseURL}/history/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 조직도 관련 API
  async getOrganization() {
    const response = await fetch(`${this.baseURL}/organization`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createOrganization(organizationData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(organizationData);
    
    const response = await fetch(`${this.baseURL}/organization`, {
      method: 'POST',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async updateOrganization(id, organizationData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(organizationData);
    
    console.log('=== UPDATE ORGANIZATION DEBUG ===');
    console.log('Organization ID:', id);
    console.log('Organization Data:', organizationData);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    
    // 조직도는 단일 리소스이므로 ID 없이 업데이트 시도
    // 여러 경로를 시도 (서버 API 구조에 따라 다를 수 있음)
    const urls = [
      `${this.baseURL}/organization`, // ID 없이
      `${this.baseURL}/organization/${id}`, // ID 포함
    ];
    
    let lastError = null;
    
    for (const url of urls) {
      try {
        console.log('Trying URL:', url);
        
        const response = await fetch(url, {
          method: 'PATCH',
          headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
          body: formData,
        });
        
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        // 응답 본문 확인
        const responseText = await response.text();
        console.log('Response text:', responseText);
        
        if (!response.ok) {
          // 404가 아니면 즉시 에러 던지기
          if (response.status !== 404) {
            throw new Error(`HTTP ${response.status}: ${responseText}`);
          }
          // 404면 다음 URL 시도
          lastError = new Error(`HTTP ${response.status}: ${responseText}`);
          continue;
        }
        
        // 성공 시
        // 빈 응답이거나 JSON이 아닐 수 있으므로 안전하게 처리
        if (!responseText || responseText.trim() === '') {
          return { success: true };
        }
        
        try {
          return JSON.parse(responseText);
        } catch (e) {
          // JSON 파싱 실패 시 성공으로 간주 (서버에서 저장은 성공했으므로)
          console.warn('Response is not JSON, but status is OK:', responseText);
          return { success: true, message: responseText };
        }
      } catch (error) {
        console.error(`Failed for URL ${url}:`, error);
        lastError = error;
        // 404가 아니면 즉시 에러 던지기
        if (!error.message.includes('404')) {
          throw error;
        }
      }
    }
    
    // 모든 URL 시도 실패
    throw lastError || new Error('모든 업데이트 경로 시도에 실패했습니다.');
  }

  // 강사진 관련 API
  async getTeam(page = 1, limit = 1000) {
    const response = await fetch(`${this.baseURL}/team?page=${page}&limit=${limit}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createTeam(teamData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(teamData);
    
    const response = await fetch(`${this.baseURL}/team`, {
      method: 'POST',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async updateTeam(id, teamData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(teamData);
    
    const response = await fetch(`${this.baseURL}/team/${id}`, {
      method: 'PATCH', // PUT 대신 PATCH 사용
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    return this.handleResponse(response);
  }

  async deleteTeam(id) {
    const response = await fetch(`${this.baseURL}/team/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 올해의 컬러 관련 API
  async getYearColor() {
    const response = await fetch(`${this.baseURL}/year-color`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getYearColorByYear(year) {
    const response = await fetch(`${this.baseURL}/year-color/${year}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async createYearColor(yearColorData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(yearColorData);
    
    console.log('=== CREATE YEAR COLOR DEBUG ===');
    console.log('Request URL:', `${this.baseURL}/year-color`);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
    }
    
    const response = await fetch(`${this.baseURL}/year-color`, {
      method: 'POST',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return this.handleResponse(response);
  }

  async updateYearColor(year, yearColorData) {
    // FormData로 변환 (파일 업로드)
    const formData = createFormData(yearColorData);
    
    console.log('=== UPDATE YEAR COLOR DEBUG ===');
    console.log('Request URL:', `${this.baseURL}/year-color/${year}`);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
    }
    
    const response = await fetch(`${this.baseURL}/year-color/${year}`, {
      method: 'PATCH',
      headers: this.getHeaders(false), // Content-Type 제외 (FormData가 자동 설정)
      body: formData,
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return this.handleResponse(response);
  }

  async deleteYearColor(year) {
    const response = await fetch(`${this.baseURL}/year-color/${year}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 회원가입 API (인증 불필요)
  async signup(userData) {
    try {
      console.log('=== SIGNUP DEBUG ===');
      console.log('Request URL:', `${this.baseURL}/user/join`);
      console.log('User data:', { ...userData, password: '***' });
      
      const response = await fetch(`${this.baseURL}/user/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage = '회원가입에 실패했습니다.';
        
        try {
          const errorData = JSON.parse(errorText);
          const serverMessage = errorData.message || errorData.error || '';
          
          // 서버 메시지를 사용자 친화적으로 변환
          if (serverMessage.includes('already exists') || serverMessage.includes('이미 존재')) {
            errorMessage = '이미 가입된 이메일입니다.';
          } else if (serverMessage.includes('validation') || serverMessage.includes('유효성')) {
            errorMessage = '입력한 정보를 확인해주세요.';
          } else if (serverMessage) {
            // 서버 메시지가 있으면 그대로 사용 (한국어 메시지인 경우)
            errorMessage = serverMessage;
          }
        } catch (parseError) {
          // JSON 파싱 실패 시 기본 메시지 사용
          console.error('Failed to parse error response:', parseError);
        }
        
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log('Signup success:', data);
      return data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  // 사용자 로그인 API
  async loginUser(email, password) {
    try {
      console.log('=== USER LOGIN DEBUG ===');
      console.log('Request URL:', `${this.baseURL}/auth/login`);
      
      const response = await fetch(`${this.baseURL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || errorData.error || `로그인 실패 (${response.status})`);
        } catch {
          throw new Error(`로그인 실패 (${response.status}): ${errorText}`);
        }
      }
      
      const data = await response.json();
      console.log('Login response data:', data);
      
      // 토큰 저장 (스네이크 케이스 필드명 지원)
      const accessToken = data.accessToken || data.access_token;
      const refreshToken = data.refreshToken || data.refresh_token;
      
      if (accessToken) {
        localStorage.setItem('userToken', accessToken);
        console.log('Access token saved to localStorage');
      } else {
        console.warn('No access token in response:', data);
      }
      
      if (refreshToken) {
        localStorage.setItem('userRefreshToken', refreshToken);
        console.log('Refresh token saved to localStorage');
      }
      
      // 저장 확인
      const savedToken = localStorage.getItem('userToken');
      console.log('Token saved check:', savedToken ? 'Token exists' : 'Token NOT saved');
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // 사용자 토큰 재발급
  async refreshUserToken(refreshToken) {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh?refreshToken=${refreshToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('토큰 재발급 실패');
      }
      
      const data = await response.json();
      
      if (data.accessToken) {
        localStorage.setItem('userToken', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('userRefreshToken', data.refreshToken);
      }
      
      return data;
    } catch (error) {
      console.error('Refresh token error:', error);
      throw error;
    }
  }

  // 사용자 로그아웃
  async logoutUser() {
    try {
      const token = localStorage.getItem('userToken');
      if (token) {
        await fetch(`${this.baseURL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('userToken');
      localStorage.removeItem('userRefreshToken');
      localStorage.removeItem('userInfo');
    }
  }

  // 사용자 토큰 검증
  async validateUserToken() {
    try {
      const token = localStorage.getItem('userToken');
      if (!token) {
        return false;
      }
      
      const response = await fetch(`${this.baseURL}/auth/validate-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      return response.ok;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // 사용자 정보 가져오기
  async getUserInfo() {
    try {
      const token = localStorage.getItem('userToken');
      console.log('getUserInfo - Token check:', token ? 'Token exists' : 'Token NOT found');
      
      if (!token) {
        console.error('getUserInfo - No token found in localStorage');
        throw new Error('로그인이 필요합니다.');
      }
      
      console.log('getUserInfo - Request URL:', `${this.baseURL}/user/myinfo`);
      console.log('getUserInfo - Authorization header:', `Bearer ${token.substring(0, 20)}...`);
      
      const response = await fetch(`${this.baseURL}/user/myinfo`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('getUserInfo - Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('userToken');
          localStorage.removeItem('userRefreshToken');
          localStorage.removeItem('userInfo');
          throw new Error('로그인이 만료되었습니다.');
        }
        throw new Error('사용자 정보를 가져오는데 실패했습니다.');
      }
      
      const data = await response.json();
      // 사용자 정보를 localStorage에 저장
      localStorage.setItem('userInfo', JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Get user info error:', error);
      throw error;
    }
  }

  // 사용자 인증 확인
  isUserAuthenticated() {
    return !!localStorage.getItem('userToken');
  }

  // 저장된 사용자 정보 가져오기
  getStoredUserInfo() {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // 사용자 관리 API (관리자 전용)
  async getPendingUsers() {
    const response = await fetch(`${this.baseURL}/user/pending`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getAllUsers() {
    const response = await fetch(`${this.baseURL}/user/all`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async approveUser(userId) {
    const response = await fetch(`${this.baseURL}/user/approve/${userId}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({}),
    });
    return this.handleResponse(response);
  }

  async rejectUser(userId, rejectionReason) {
    const response = await fetch(`${this.baseURL}/user/reject/${userId}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ rejectionReason }),
    });
    return this.handleResponse(response);
  }

  async deleteUser(userId) {
    const response = await fetch(`${this.baseURL}/user/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // 승인 완료된 회원을 다시 승인 대기 상태로 변경
  async resetUserStatus(userId) {
    const response = await fetch(`${this.baseURL}/user/reset-status/${userId}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({}),
    });
    return this.handleResponse(response);
  }

  // 문의 요청 관련 API
  async submitRequest(requestData) {
    try {
      const response = await fetch(`${this.baseURL}/request/submit`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestData),
      });
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('Submit request error:', error);
      throw error;
    }
  }

  // 로그아웃
  logout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('refreshToken');
  }

  // 토큰 유효성 검사
  isAuthenticated() {
    return !!localStorage.getItem('adminToken');
  }
}

const apiService = new ApiService();
export default apiService;
